'use client';
import { useCallback, useEffect, useRef } from 'react';
import Script from 'next/script';

const toCssHeight = (value, fallback) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `${value}px`;
  }

  if (typeof value !== 'string' || !value.trim()) {
    return fallback;
  }

  const height = value.trim();
  return /^\d+(?:\.\d+)?$/.test(height) ? `${height}px` : height;
};

const Calendly = ({ block, position }) => {
  const embedRef = useRef(null);
  const { _metadata, reference } = block.calendly;
  const calendlyReference = reference?.[0];
  const calendlyUrl = calendlyReference?.data_url;
  const backgroundColor = calendlyReference?.background_color;
  const mobileHeight = calendlyReference?.mobile_height;
  const tabletHeight = calendlyReference?.tablet_height;
  const desktopHeight = calendlyReference?.desktop_height;
  const mobileHeightCss = toCssHeight(mobileHeight, '600px');
  const desktopHeightCss = toCssHeight(desktopHeight, '700px');
  const tabletHeightCss = toCssHeight(tabletHeight, '1050px');

  const initializeWidget = useCallback(() => {
    if (!calendlyUrl || !embedRef.current || !window.Calendly) {
      return;
    }

    embedRef.current.replaceChildren();
    window.Calendly.initInlineWidget({
      url: calendlyUrl,
      parentElement: embedRef.current
    });
  }, [calendlyUrl]);

  useEffect(() => {
    const embedElement = embedRef.current;
    initializeWidget();

    return () => {
      embedElement?.replaceChildren();
    };
  }, [initializeWidget]);

  return (
    <section
      id={`${_metadata.uid}_${position}`}
      className={` bg-[${backgroundColor}]`}
    >
      <div className="flex justify-center px-0 sm:p-10 sm:py-10">
        <div
          ref={embedRef}
          className="h-(--calendly-mobile-height) w-full min-w-80 md:h-(--calendly-tablet-height) xl:h-(--calendly-desktop-height)"
          style={{
            '--calendly-mobile-height': mobileHeightCss,
            '--calendly-tablet-height': tabletHeightCss,
            '--calendly-desktop-height': desktopHeightCss
          }}
        />
        <Script
          src="https://assets.calendly.com/assets/external/widget.js"
          strategy="afterInteractive"
          onReady={initializeWidget}
        />
      </div>
    </section>
  );
};

export default Calendly;
